import type {
  TimeSeriesCounterpartiesDataPoint,
  TimeSeriesDataPoint,
} from "@/apis/gql/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { TimeGranularity } from "@/lib/ledger/dashboard-analytics";
import { formatPeriodLabel } from "@/lib/ledger/dashboard-analytics";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";

type GroupingMode = "accounts" | "counterparties";

interface IncomeExpenseChartProps {
  accountsData: TimeSeriesDataPoint[];
  counterpartiesData: TimeSeriesCounterpartiesDataPoint[];
  currency?: string;
  title?: string;
  description?: string;
  granularity: TimeGranularity;
  onGranularityChange: (granularity: TimeGranularity) => void;
}

const colorPalette = [
  "hsl(142, 76%, 45%)", // Green
  "hsl(262, 52%, 47%)", // Purple
  "hsl(197, 71%, 52%)", // Blue
  "hsl(24, 95%, 53%)", // Orange
  "hsl(48, 96%, 53%)", // Yellow
  "hsl(340, 75%, 55%)", // Pink
  "hsl(171, 68%, 41%)", // Teal
  "hsl(14, 91%, 51%)", // Red-orange
  "hsl(199, 89%, 48%)", // Sky blue
  "hsl(283, 52%, 58%)", // Lavender
  "hsl(84, 61%, 51%)", // Lime
  "hsl(0, 84%, 60%)", // Red
  "hsl(165, 82%, 51%)", // Turquoise
  "hsl(45, 93%, 47%)", // Gold
  "hsl(328, 86%, 53%)", // Magenta
  "hsl(207, 90%, 54%)", // Dodger blue
  "hsl(291, 64%, 42%)", // Dark violet
  "hsl(122, 39%, 49%)", // Forest green
  "hsl(39, 100%, 50%)", // Amber
  "hsl(355, 78%, 56%)", // Crimson
];

type ViewMode = "income" | "expense";

export function IncomeExpenseChart({
  accountsData,
  counterpartiesData,
  currency = "EUR",
  title = "Erlöse und Aufwendungen",
  description = "Einnahmen und Ausgaben nach Konten oder Debitoren/Kreditoren über den gewählten Zeitraum.",
  granularity,
  onGranularityChange,
}: IncomeExpenseChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("income");
  const [groupingMode, setGroupingMode] = useState<GroupingMode>("accounts");

  // Choose data based on grouping mode
  const data = groupingMode === "accounts" ? accountsData : counterpartiesData;

  // Collect all unique items (accounts or counterparties) across all periods
  const allIncomeItems = new Map<string, { name: string; number?: string }>();
  const allExpenseItems = new Map<string, { name: string; number?: string }>();

  if (groupingMode === "accounts") {
    // Process accounts data
    for (const point of accountsData) {
      for (const acc of point.incomeByAccount) {
        if (!allIncomeItems.has(acc.accountId)) {
          allIncomeItems.set(acc.accountId, {
            name: acc.accountName,
            number: acc.accountNumber,
          });
        }
      }
      for (const acc of point.expensesByAccount) {
        if (!allExpenseItems.has(acc.accountId)) {
          allExpenseItems.set(acc.accountId, {
            name: acc.accountName,
            number: acc.accountNumber,
          });
        }
      }
    }
  } else {
    // Process counterparties data
    for (const point of counterpartiesData) {
      for (const cp of point.incomeByCounterparty) {
        if (!allIncomeItems.has(cp.entityId)) {
          allIncomeItems.set(cp.entityId, {
            name: cp.entityName,
          });
        }
      }
      for (const cp of point.expensesByCounterparty) {
        if (!allExpenseItems.has(cp.entityId)) {
          allExpenseItems.set(cp.entityId, {
            name: cp.entityName,
          });
        }
      }
    }
  }

  // Build chart configuration dynamically with proper display names
  const chartConfig: ChartConfig = {};
  const incomeItemIds = Array.from(allIncomeItems.keys());
  const expenseItemIds = Array.from(allExpenseItems.keys());

  incomeItemIds.forEach((itemId, index) => {
    const item = allIncomeItems.get(itemId);
    if (!item) return;
    const displayName = item.number
      ? `${item.number} - ${item.name}`
      : item.name;
    chartConfig[`income_${itemId}`] = {
      label: displayName,
      color: colorPalette[index % colorPalette.length],
    };
  });

  expenseItemIds.forEach((itemId, index) => {
    const item = allExpenseItems.get(itemId);
    if (!item) return;
    const displayName = item.number
      ? `${item.number} - ${item.name}`
      : item.name;
    chartConfig[`expense_${itemId}`] = {
      label: displayName,
      color: colorPalette[index % colorPalette.length],
    };
  });

  // Transform data for stacked bars
  const chartData = data.map((point) => {
    const dataPoint: Record<string, string | Date | number> = {
      period: formatPeriodLabel(point.period, granularity),
      date: point.date,
    };

    if (groupingMode === "accounts") {
      // Add income accounts
      for (const acc of (point as TimeSeriesDataPoint).incomeByAccount) {
        dataPoint[`income_${acc.accountId}`] = acc.amount;
      }

      // Add expense accounts
      for (const acc of (point as TimeSeriesDataPoint).expensesByAccount) {
        dataPoint[`expense_${acc.accountId}`] = acc.amount;
      }
    } else {
      // Add income counterparties
      for (const cp of (point as TimeSeriesCounterpartiesDataPoint)
        .incomeByCounterparty) {
        dataPoint[`income_${cp.entityId}`] = cp.amount;
      }

      // Add expense counterparties
      for (const cp of (point as TimeSeriesCounterpartiesDataPoint)
        .expensesByCounterparty) {
        dataPoint[`expense_${cp.entityId}`] = cp.amount;
      }
    }

    return dataPoint;
  });

  // Calculate totals
  const totalIncome = (data as Array<{ totalIncome: number }>).reduce(
    (sum, point) => sum + point.totalIncome,
    0,
  );
  const totalExpenses = (data as Array<{ totalExpenses: number }>).reduce(
    (sum, point) => sum + point.totalExpenses,
    0,
  );
  const netProfit = totalIncome - totalExpenses;

  // Get the item IDs to render based on view mode
  const itemIdsToRender =
    viewMode === "income" ? incomeItemIds : expenseItemIds;
  const stackId = viewMode === "income" ? "income" : "expense";

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl font-semibold mb-1">
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[400px] text-muted-foreground">
            Keine Daten für den ausgewählten Zeitraum verfügbar
          </div>
        ) : (
          <div className="flex gap-8">
            {/* Chart */}
            <div className="flex-1">
              <div className="mb-6 flex items-center justify-between">
                <Tabs
                  value={viewMode}
                  onValueChange={(value) => setViewMode(value as ViewMode)}
                >
                  <TabsList>
                    <TabsTrigger value="income">Erlöse</TabsTrigger>
                    <TabsTrigger value="expense">Aufwendungen</TabsTrigger>
                  </TabsList>
                </Tabs>

                <div className="flex items-center gap-6">
                  {/* Grouping mode switch */}
                  <div className="flex items-center gap-3">
                    <Label
                      htmlFor="grouping-mode"
                      className="text-sm text-muted-foreground whitespace-nowrap"
                    >
                      Nach Konten
                    </Label>
                    <Switch
                      id="grouping-mode"
                      checked={groupingMode === "counterparties"}
                      onCheckedChange={(checked) =>
                        setGroupingMode(checked ? "counterparties" : "accounts")
                      }
                    />
                    <Label
                      htmlFor="grouping-mode"
                      className="text-sm text-muted-foreground whitespace-nowrap"
                    >
                      Nach Debitoren/Kreditoren
                    </Label>
                  </div>

                  {/* Time granularity selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Auflösung:
                    </span>
                    <Select
                      value={granularity}
                      onValueChange={(value) =>
                        onGranularityChange(value as TimeGranularity)
                      }
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Täglich</SelectItem>
                        <SelectItem value="weekly">Wöchentlich</SelectItem>
                        <SelectItem value="monthly">Monatlich</SelectItem>
                        <SelectItem value="quarterly">Quartalsweise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <ChartContainer config={chartConfig} className="h-[500px] w-full">
                <BarChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    className="stroke-muted/30"
                  />
                  <XAxis
                    dataKey="period"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={12}
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={12}
                    tickFormatter={(value) =>
                      new Intl.NumberFormat("de-DE", {
                        notation: "compact",
                        compactDisplay: "short",
                      }).format(value)
                    }
                    className="text-xs"
                    tick={{ fill: "hsl(var(--muted-foreground))" }}
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        labelFormatter={(label) => label}
                        formatter={(value, name) => {
                          const numValue = Number(value);
                          if (numValue === 0) return null; // Don't show zero values
                          const formattedValue = new Intl.NumberFormat(
                            "de-DE",
                            {
                              style: "currency",
                              currency,
                            },
                          ).format(numValue);
                          const label =
                            chartConfig[name as keyof typeof chartConfig]
                              ?.label || name;
                          return [`${label}: ${formattedValue}`, null];
                        }}
                      />
                    }
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "20px" }}
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      // Filter based on current view mode and only show non-zero values
                      const prefix =
                        viewMode === "income" ? "income_" : "expense_";
                      const relevantItems = payload.filter(
                        (item) =>
                          typeof item.dataKey === "string" &&
                          item.dataKey.startsWith(prefix),
                      );

                      return (
                        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs justify-center mt-4">
                          {relevantItems.map((item) => {
                            // Get the proper label from chartConfig using the dataKey
                            const dataKey = item.dataKey as string;
                            const label =
                              chartConfig[dataKey]?.label || item.value;

                            return (
                              <div
                                key={dataKey}
                                className="flex items-center gap-1.5"
                              >
                                <div
                                  className="w-3 h-3 rounded"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span className="text-muted-foreground">
                                  {label}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      );
                    }}
                  />
                  {itemIdsToRender.map((itemId, index) => (
                    <Bar
                      key={`${viewMode}_${itemId}`}
                      dataKey={`${viewMode}_${itemId}`}
                      stackId={stackId}
                      fill={colorPalette[index % colorPalette.length]}
                      radius={
                        index === itemIdsToRender.length - 1 ? [4, 4, 0, 0] : 0
                      }
                    />
                  ))}
                </BarChart>
              </ChartContainer>
            </div>

            <div className="flex flex-col justify-center gap-4 min-w-[240px]">
              {/* Income Card */}
              <div className="rounded-lg bg-card border border-border p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <div className="w-3 h-3 rounded-full bg-[hsl(142,76%,45%)]" />
                  Erlöse
                </div>
                <div className="text-3xl font-semibold text-foreground">
                  {new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency,
                  }).format(totalIncome)}
                </div>
              </div>

              {/* Expenses Card */}
              <div className="rounded-lg bg-card border border-border p-4 space-y-2">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  <div className="w-3 h-3 rounded-full bg-[hsl(262,52%,47%)]" />
                  Aufwendungen
                </div>
                <div className="text-3xl font-semibold text-foreground">
                  {new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency,
                  }).format(totalExpenses)}
                </div>
              </div>

              {/* Net Profit/Loss Card */}
              <div
                className={`rounded-lg border p-4 space-y-2 ${
                  netProfit >= 0
                    ? "bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-900"
                    : "bg-rose-50/50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900"
                }`}
              >
                <div
                  className={`flex items-center gap-2 text-xs font-medium uppercase tracking-wide ${
                    netProfit >= 0
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-rose-700 dark:text-rose-400"
                  }`}
                >
                  {netProfit >= 0 ? (
                    <TrendingUp className="w-4 h-4" />
                  ) : (
                    <TrendingDown className="w-4 h-4" />
                  )}
                  {netProfit >= 0 ? "Überschuss" : "Defizit"}
                </div>
                <div
                  className={`text-3xl font-semibold ${
                    netProfit >= 0
                      ? "text-emerald-900 dark:text-emerald-100"
                      : "text-rose-900 dark:text-rose-100"
                  }`}
                >
                  {new Intl.NumberFormat("de-DE", {
                    style: "currency",
                    currency,
                  }).format(Math.abs(netProfit))}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
